/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-sql-foreign-key-constraint-visualizer-fr block)");
/* eslint-enable no-console */


(function () {
    // ── Données initiales ──────────────────────────────────
    const clients = [
        { id: 1, nom: "Alice" },
        { id: 2, nom: "Bob" }
    ];

    const commandes = [
        { id: 101, client_id: 1, montant: 150 },
        { id: 102, client_id: 2, montant: 80 }
    ];

    const COLS_CLIENTS = ["id", "nom"];
    const COLS_COMMANDES = ["id", "client_id", "montant"];

    // ── Helpers ────────────────────────────────────────────
    function tableHtml(data, cols, stateMap) {
        const thead = cols.map(c => `<th>${c}</th>`).join("");
        const rows = data.map(r => {
            const cls = (stateMap && r.id !== undefined) ? (stateMap[r.id] || "") : "";
            return `<tr class="${cls}">${cols.map(c => `<td>${r[c] !== undefined ? r[c] : "NULL"}</td>`).join("")}</tr>`;
        }).join("");
        return `<table class="mini"><thead><tr>${thead}</tr></thead><tbody>${rows}</tbody></table>`;
    }

    function blockHtml(headerCls, headerLabel, content) {
        return `<div class="table-block">
                <div class="table-block-header ${headerCls}">${headerLabel}</div>
                <div class="table-block-body">${content}</div>
            </div>`;
    }

    function legendHtml(items) {
        if (!items.length) return "";
        const spans = items.map(([color, label]) =>
            `<span><i class="legend-dot" style="background:${color};border:1px solid #555;"></i> ${label}</span>`
        ).join("");
        return `<div class="legend">${spans}</div>`;
    }

    function bannerHtml(type, icon, text) {
        return `<div class="op-banner ${type}">${icon} ${text}</div>`;
    }

    // ── Scénario Foreign Key ──────────────────────────────
    const query = {
        parts: [
            { id: "fk-create-clients", text: "CREATE TABLE clients (\n  id INT PRIMARY KEY,\n  nom VARCHAR(50)\n);", cls: "constraint" },
            { id: "fk-create-commandes", text: "CREATE TABLE commandes (\n  id INT,\n  client_id INT,\n  montant DECIMAL(10,2),\n  FOREIGN KEY (client_id) REFERENCES clients(id)\n);", cls: "constraint" },
            { id: "fk-ins-client1", text: "INSERT INTO clients VALUES (1, 'Alice');", cls: "" },
            { id: "fk-ins-client2", text: "INSERT INTO clients VALUES (2, 'Bob');", cls: "" },
            { id: "fk-ins-good", text: "INSERT INTO commandes VALUES (101, 1, 150.00);", cls: "" },
            { id: "fk-ins-bad", text: "INSERT INTO commandes VALUES (102, 99, 200.00); -- ⚠️ échoue", cls: "danger" }
        ],
        steps: [
            {
                key: "fk-create-clients",
                sql: "Création de la table clients (table parent)",
                desc: "On crée la table <code>clients</code> avec une clef primaire <code>id</code>.",
                render: () => {
                    return bannerHtml("success", "✅", "Table clients créée — base de référence") +
                        `<div class="tables-grid">
                                ${blockHtml("normal", "📋 clients", tableHtml([], COLS_CLIENTS, null))}
                            </div>`;
                }
            },
            {
                key: "fk-create-commandes",
                sql: "Création de la table commandes avec clé étrangère",
                desc: "La colonne <code>client_id</code> est déclarée comme <strong>FOREIGN KEY vers clients(id)</strong>.",
                render: () => {
                    const data = [{ id: 1, _state: "normal" }, { id: 2, _state: "normal" }];
                    return bannerHtml("success", "✅", "Table commandes créée avec FK (client_id → clients.id)") +
                        `<div class="tables-grid">
                                ${blockHtml("normal", "📋 clients", tableHtml([], ["id"], null))}
                                ${blockHtml("normal", "📋 commandes", tableHtml([], COLS_COMMANDES, null))}
                            </div>`;
                }
            },
            {
                key: "fk-ins-client1",
                sql: "INSERT INTO clients — Client 1 (Alice)",
                desc: "On insère le client <code>id=1</code> dans la table de référence.",
                render: () => {
                    const data = [{ id: 1, nom: "Alice", _state: "inserted" }];
                    return bannerHtml("success", "✅", "Client 1 inséré — base de référence pour la FK") +
                        `<div class="tables-grid">
                                ${blockHtml("success", "✅ clients après Alice", tableHtml(data, COLS_CLIENTS, data.reduce((acc, r) => ({ ...acc, [r.id]: "inserted" }), {})))}
                            </div>`;
                }
            },
            {
                key: "fk-ins-client2",
                sql: "INSERT INTO clients — Client 2 (Bob)",
                desc: "On ajoute le client <code>id=2</code>. Le client 99 n'existe pas.",
                render: () => {
                    const data = [
                        { id: 1, nom: "Alice", _state: "normal" },
                        { id: 2, nom: "Bob", _state: "inserted" }
                    ];
                    const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: r._state }), {});
                    return bannerHtml("success", "✅", "Clients 1 et 2 créés — la FK peut pointer vers eux") +
                        `<div class="tables-grid">
                                ${blockHtml("success", "✅ clients complets", tableHtml(data, COLS_CLIENTS, stateMap))}
                            </div>`;
                }
            },
            {
                key: "fk-ins-good",
                sql: "INSERT INTO commandes — client_id=1 → OK",
                desc: "On insère une commande avec <code>client_id=1</code>. Ce client <strong>existe</strong> → la FK est respectée.",
                render: () => {
                    const data = [
                        { id: 1, nom: "Alice", _state: "normal" },
                        { id: 2, nom: "Bob", _state: "normal" }
                    ];
                    const dataComm = [{ id: 101, client_id: 1, montant: 150, _state: "inserted" }];
                    const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: "normal" }), {});
                    const stateMapComm = dataComm.reduce((acc, r) => ({ ...acc, [r.id]: "inserted" }), {});
                    return bannerHtml("success", "✅", "INSERT ACCEPTÉ — la clé étrangère existe (client_id=1)") +
                        `<div class="tables-grid">
                                ${blockHtml("normal", "📋 clients", tableHtml(data, COLS_CLIENTS, stateMap))}
                                ${blockHtml("success", "✅ commandes", tableHtml(dataComm, COLS_COMMANDES, stateMapComm))}
                            </div>`;
                }
            },
            {
                key: "fk-ins-bad",
                sql: "INSERT INTO commandes — client_id=99 → échec",
                desc: "On tente d'insérer une commande avec <code>client_id=99</code>. Ce client <strong>n'existe pas</strong> dans <code>clients</code> → la clé étrangère est violée.",
                render: () => {
                    const data = [
                        { id: 1, nom: "Alice", _state: "normal" },
                        { id: 2, nom: "Bob", _state: "normal" }
                    ];
                    const dataComm = [{ id: 101, client_id: 1, montant: 150, _state: "normal" }];
                    const stateMap = data.reduce((acc, r) => ({ ...acc, [r.id]: "normal" }), {});
                    const stateMapComm = dataComm.reduce((acc, r) => ({ ...acc, [r.id]: "normal" }), {});
                    return bannerHtml("failure", "❌", "INSERT REJETÉ — la clé étrangère n'existe pas (client_id=99 n'existe pas)") +
                        `<div class="tables-grid">
                                ${blockHtml("normal", "📋 clients", tableHtml(data, COLS_CLIENTS, stateMap))}
                                ${blockHtml("failure", "⚠️ commandes — inchangées", tableHtml(dataComm, COLS_COMMANDES, stateMapComm))}
                            </div>
                            <p style="font-size:11px;color:#7f1d1d;"><strong>❌ Résultat de l'opération :</strong> pas de nouvelle ligne dans commandes.</p>`;
                }
            }
        ]
    };

    // ── État global ────────────────────────────────────────
    let curStepIdx = -1;

    // ── Rendu SQL ──────────────────────────────────────────
    function renderSql() {
        document.getElementById("sql-display").innerHTML =
            query.parts.map(p =>
                `<span class="sql-part ${p.cls || ""}" id="${p.id}">${p.text}</span>`
            ).join("\n");
    }

    // ── Mise à jour UI ─────────────────────────────────────
    function updateStep(i) {
        curStepIdx = i;
        const step = query.steps[i];

        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        const el = document.getElementById(step.key);
        if (el) el.classList.add("active");

        document.getElementById("step-meta").innerHTML =
            `<strong>Étape ${i + 1} / ${query.steps.length} — ${step.sql}</strong><br>${step.desc}`;

        document.getElementById("step-output").innerHTML = step.render();
    }

    // ── Reset ──────────────────────────────────────────────
    function reset() {
        curStepIdx = -1;
        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        document.getElementById("step-meta").textContent =
            "Cliquez sur un bouton pour avancer ou reculer.";
        document.getElementById("step-output").innerHTML = "";
    }

    // ── Navigation ─────────────────────────────────────────
    document.getElementById("btn-next").addEventListener("click", () => {
        if (curStepIdx < query.steps.length - 1) updateStep(curStepIdx + 1);
    });

    document.getElementById("btn-prev").addEventListener("click", () => {
        if (curStepIdx > 0) updateStep(curStepIdx - 1);
    });

    document.getElementById("btn-reset").addEventListener("click", reset);

    // ── Init ───────────────────────────────────────────────
    renderSql();
})()